// @flow
import React, { Component } from 'react';
import { t } from '@lingui/macro';

import type { Address } from '@rover/react-lib/types';
import type {
  CreditCardAddSummary,
  CreditCardSummary,
  CreditCardUpdateSummary,
} from '@rover/react-lib/types/CreditCard';
import inject from '@rover/utilities/injector';

import FormGroup from '../../formFields/FormGroup';
import FormValidationError from '../../formFields/FormValidationError';
import SuccessOrErrorAlert from '../../notifications/SuccessOrErrorAlert';
import Form from '../../utils/Form';
import StripeCreditCardForm from '../StripeCreditCardForm';

import BraintreeCreditCardForm from './BraintreeCreditCardForm';
import SelectCreditCardForm from './SelectCreditCardForm';

const useStripeContext = inject('Rover.context.useStripe', false);
const enableUpdateCard = inject('Rover.flags.rolloutUpdateCreditCardMethod', false);

type State = {
  addingCard: boolean,
  isEditingCard: boolean,
};

export type Props = {
  address?: Address,
  onUpdateCard: ($Shape<CreditCardUpdateSummary> | $Shape<CreditCardAddSummary>) => void,
  loading: boolean,
  hideAddCardCancelButton?: boolean,
  partialPostalRegExp?: ?RegExp,
  fullPostalRegExp?: ?RegExp,
  getStateFieldNameByPostalCode?: string => string,
  isStateFieldRequiredByPostalCode?: string => boolean,
  supportedCountryCodes: Array<string>,
  language: string,

  creditCards: Array<CreditCardSummary>,
  selectedCardId: string,
  isUpdatable: boolean,
  onChange: string => void,
  onDeleteCard: string => void,
  onEditCard: CreditCardSummary => void,
  radioName?: string,

  validationErrors: any,
  nestedForm: boolean,
  errorMessage: string,
  editingCreditCard: ?CreditCardSummary,

  onReportFormVisibility: boolean => void,

  disabled: boolean,

  useStripe: boolean,
  isSubmitting: boolean,
  centerButtons?: boolean,
  saveButtonText?: string,

  enableCheckout: () => void,
  disableCheckout: () => void,
};

export default class CreditCardForm extends Component<Props, State> {
  static defaultProps = {
    step: 0,
    loading: false,
    hideAddCardCancelButton: undefined,
    nestedForm: false,
    getStateFieldNameByPostalCode: () => '',
    isStateFieldRequiredByPostalCode: () => true,

    creditCards: [],
    selectedCardId: '',
    isUpdatable: false,
    addingCard: false,
    isEditingCard: false,
    validationErrors: undefined,
    onReportFormVisibility: () => {},

    disabled: false,
    centerButtons: false,
    disableCheckout: () => {},
    enableCheckout: () => {},

    useStripe: useStripeContext,
  };

  state = {
    addingCard: false,
    isEditingCard: false,
  };

  componentDidUpdate(prevProps: Props) {
    const { selectedCardId, onChange } = this.props;
    if (selectedCardId && !prevProps.selectedCardId) {
      onChange(selectedCardId);
    }
  }

  static validators = {
    selectedCardId: Form.validators.required(t`Credit card is required`),
  };

  handleToggle = (isAdding: boolean) => {
    const { onReportFormVisibility } = this.props;
    onReportFormVisibility(isAdding);
    this.setState(() => ({
      addingCard: isAdding,
    }));
  };

  editCardPopup = (data: CreditCardSummary) => {
    const { onEditCard, disableCheckout } = this.props;
    this.setState(() => ({
      isEditingCard: true,
    }));
    disableCheckout();
    onEditCard(data);
  };

  shouldShowForm = (): boolean => {
    if (this.state.addingCard || this.state.isEditingCard) {
      return true;
    }

    if (!this.props.loading && this.props.creditCards.length === 0) {
      this.setState(() => ({ addingCard: true, isEditingCard: false }));
      return true;
    }
    return false;
  };

  editFormClose = () => {
    const { enableCheckout } = this.props;
    this.setState({
      isEditingCard: false,
    });
    enableCheckout();
  };

  // Rerender when the submit button is pressed to notify FormValidationError
  onSubmitButtonPress = () => {
    this.forceUpdate();
  };

  render() {
    const {
      address,
      // add card
      loading,
      hideAddCardCancelButton,
      partialPostalRegExp,
      fullPostalRegExp,
      getStateFieldNameByPostalCode,
      isStateFieldRequiredByPostalCode,
      supportedCountryCodes,
      language,

      // select card
      creditCards,
      selectedCardId,
      isUpdatable,
      onDeleteCard,
      onEditCard,
      onChange,
      radioName,

      validationErrors,
      nestedForm,
      errorMessage,

      onReportFormVisibility,
      disabled,

      useStripe,
      isSubmitting,
      centerButtons,
      saveButtonText,

      // edit card
      onUpdateCard,
      editingCreditCard,

      ...others
    } = this.props;
    let creditCardElement;

    if (this.shouldShowForm()) {
      const formProps = {
        address,
        validationErrors,
        loading,
        nestedForm,
        hideCancelButton: hideAddCardCancelButton,
        fullPostalRegExp,
        partialPostalRegExp,
        getStateFieldNameByPostalCode,
        isStateFieldRequiredByPostalCode,
        isSubmitting,
        supportedCountryCodes,
        language,
        centerButtons,
        saveButtonText,
        errorMessage,
        onSubmit: onUpdateCard,
      };

      if (useStripe) {
        if (this.state.isEditingCard) {
          creditCardElement = (
            <StripeCreditCardForm
              isEditing
              cardDetails={editingCreditCard}
              onToggleForm={() => this.editFormClose()}
              onSubmitButtonPress={() => this.onSubmitButtonPress()}
              {...formProps}
            />
          );
        } else {
          creditCardElement = (
            <StripeCreditCardForm
              onToggleForm={() => this.handleToggle(false)}
              onSubmitButtonPress={() => this.onSubmitButtonPress()}
              {...formProps}
            />
          );
        }
      } else {
        creditCardElement = (
          <BraintreeCreditCardForm onToggleForm={() => this.handleToggle(false)} {...formProps} />
        );
      }
    } else {
      creditCardElement = (
        <SelectCreditCardForm
          cards={creditCards}
          value={selectedCardId}
          isUpdatable={enableUpdateCard}
          onChange={onChange}
          onDelete={onDeleteCard}
          onEdit={data => this.editCardPopup(data)}
          onToggleForm={() => this.handleToggle(true)}
          radioName={radioName}
          disabled={disabled}
        />
      );
    }

    return (
      <FormGroup {...others}>
        <FormValidationError
          message={
            validationErrors && (validationErrors.selectedCardId || validationErrors.formVisible)
          }
          didSubmit
          canDismiss
        >
          <SuccessOrErrorAlert error={errorMessage} />
          {creditCardElement}
        </FormValidationError>
      </FormGroup>
    );
  }
}



// WEBPACK FOOTER //
// ../react-lib/src/components/forms/CreditCardForm/CreditCardForm.jsx